import { useMemo, useState } from "react";
import { Search, X } from "lucide-react";
import { Input } from "@/shared/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { Button } from "@/shared/components/ui/button";
import { Skeleton } from "@/shared/components/ui/skeleton";
import { useDebounce } from "@/shared/hooks/useDebounce";
import type { DataForwarderPolicy } from "../types/preferences_types";
import { DataForwarderPolicyCard } from "./DataForwarderPolicyCard";

type ForwardingTypeFilter = "all" | "s3" | "gcs";
type PhaseFilter = "all" | "in_progress" | "complete";
type SortOption = "name_asc" | "id_desc" | "phase_asc";

interface DataForwarderPolicyCardListProps {
  policies: DataForwarderPolicy[];
  isLoading: boolean;
  onCardClick: (policy: DataForwarderPolicy) => void;
}

const SORT_LABELS: Record<SortOption, string> = {
  name_asc: "이름순",
  id_desc: "최신순 (ID)",
  phase_asc: "등록 단계순",
};

export function DataForwarderPolicyCardList({
  policies,
  isLoading,
  onCardClick,
}: DataForwarderPolicyCardListProps) {
  const [searchInput, setSearchInput] = useState("");
  const debouncedSearch = useDebounce(searchInput, 300);
  const [typeFilter, setTypeFilter] = useState<ForwardingTypeFilter>("all");
  const [phaseFilter, setPhaseFilter] = useState<PhaseFilter>("all");
  const [sortOption, setSortOption] = useState<SortOption>("name_asc");

  const filteredAndSorted = useMemo(() => {
    let result = [...policies];

    if (debouncedSearch) {
      const q = debouncedSearch.toLowerCase();
      result = result.filter(
        (p) =>
          p.policy_name?.toLowerCase().includes(q) ||
          p.org_id?.toLowerCase().includes(q) ||
          p.workspace_id?.toLowerCase().includes(q),
      );
    }

    if (typeFilter !== "all") {
      result = result.filter((p) => p.forwarding_type === typeFilter);
    }

    if (phaseFilter === "complete") {
      result = result.filter((p) => p.register_phase === 3);
    } else if (phaseFilter === "in_progress") {
      result = result.filter((p) => p.register_phase !== 3);
    }

    result.sort((a, b) => {
      switch (sortOption) {
        case "name_asc":
          return (a.policy_name ?? "").localeCompare(b.policy_name ?? "");
        case "id_desc":
          return b.id - a.id;
        case "phase_asc":
          return (a.register_phase ?? 0) - (b.register_phase ?? 0);
        default:
          return 0;
      }
    });

    return result;
  }, [policies, debouncedSearch, typeFilter, phaseFilter, sortOption]);

  const hasActiveFilters = debouncedSearch || typeFilter !== "all" || phaseFilter !== "all";

  const resetFilters = () => {
    setSearchInput("");
    setTypeFilter("all");
    setPhaseFilter("all");
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Toolbar
          searchInput={searchInput}
          onSearchChange={setSearchInput}
          typeFilter={typeFilter}
          onTypeFilterChange={setTypeFilter}
          phaseFilter={phaseFilter}
          onPhaseFilterChange={setPhaseFilter}
          sortOption={sortOption}
          onSortChange={setSortOption}
          hasActiveFilters={false}
          onResetFilters={resetFilters}
        />
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <CardSkeleton key={i} />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <Toolbar
        searchInput={searchInput}
        onSearchChange={setSearchInput}
        typeFilter={typeFilter}
        onTypeFilterChange={setTypeFilter}
        phaseFilter={phaseFilter}
        onPhaseFilterChange={setPhaseFilter}
        sortOption={sortOption}
        onSortChange={setSortOption}
        hasActiveFilters={!!hasActiveFilters}
        onResetFilters={resetFilters}
      />

      {filteredAndSorted.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-lg border border-dashed py-16">
          <p className="text-lg font-medium text-muted-foreground">
            {hasActiveFilters ? "검색 결과가 없습니다." : "등록된 정책이 없습니다."}
          </p>
          {hasActiveFilters && (
            <Button variant="link" className="mt-2" onClick={resetFilters}>
              필터 초기화
            </Button>
          )}
        </div>
      ) : (
        <>
          <p className="text-sm text-muted-foreground">
            총 {filteredAndSorted.length}개
            {hasActiveFilters && ` (전체 ${policies.length}개 중)`}
          </p>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            {filteredAndSorted.map((policy) => (
              <DataForwarderPolicyCard
                key={policy.id}
                policy={policy}
                onClick={onCardClick}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function Toolbar({
  searchInput,
  onSearchChange,
  typeFilter,
  onTypeFilterChange,
  phaseFilter,
  onPhaseFilterChange,
  sortOption,
  onSortChange,
  hasActiveFilters,
  onResetFilters,
}: {
  searchInput: string;
  onSearchChange: (v: string) => void;
  typeFilter: ForwardingTypeFilter;
  onTypeFilterChange: (v: ForwardingTypeFilter) => void;
  phaseFilter: PhaseFilter;
  onPhaseFilterChange: (v: PhaseFilter) => void;
  sortOption: SortOption;
  onSortChange: (v: SortOption) => void;
  hasActiveFilters: boolean;
  onResetFilters: () => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-3">
      <div className="relative w-64">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={searchInput}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder="정책 이름, ORG ID 검색..."
          className="pl-9"
        />
      </div>

      <Select value={typeFilter} onValueChange={(v) => onTypeFilterChange(v as ForwardingTypeFilter)}>
        <SelectTrigger className="w-36">
          <SelectValue placeholder="전송 타입" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">전체 타입</SelectItem>
          <SelectItem value="s3">S3</SelectItem>
          <SelectItem value="gcs">GCS</SelectItem>
        </SelectContent>
      </Select>

      <Select value={phaseFilter} onValueChange={(v) => onPhaseFilterChange(v as PhaseFilter)}>
        <SelectTrigger className="w-36">
          <SelectValue placeholder="등록 상태" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">전체 상태</SelectItem>
          <SelectItem value="in_progress">진행중</SelectItem>
          <SelectItem value="complete">등록 완료</SelectItem>
        </SelectContent>
      </Select>

      <Select value={sortOption} onValueChange={(v) => onSortChange(v as SortOption)}>
        <SelectTrigger className="w-40">
          <SelectValue placeholder="정렬" />
        </SelectTrigger>
        <SelectContent>
          {(Object.entries(SORT_LABELS) as [SortOption, string][]).map(([key, label]) => (
            <SelectItem key={key} value={key}>
              {label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {hasActiveFilters && (
        <Button variant="ghost" size="sm" onClick={onResetFilters}>
          <X className="mr-1 h-4 w-4" />
          초기화
        </Button>
      )}
    </div>
  );
}

function CardSkeleton() {
  return (
    <div className="rounded-lg border-2 border-muted p-6 space-y-4">
      <div className="flex items-center justify-between">
        <Skeleton className="h-6 w-20" />
        <Skeleton className="h-5 w-16 rounded-full" />
      </div>
      <Skeleton className="h-6 w-48" />
      <div className="space-y-2">
        <div className="flex justify-between">
          <Skeleton className="h-4 w-16" />
          <Skeleton className="h-4 w-24" />
        </div>
        <div className="flex justify-between">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-4 w-20" />
        </div>
        <div className="flex justify-between">
          <Skeleton className="h-4 w-16" />
          <Skeleton className="h-4 w-28" />
        </div>
      </div>
    </div>
  );
}
